import React from 'react';

import { LayoutComposer, NamedLink } from '../../components';
import GemstoneSiteFooter from '../../components/GemstoneSiteFooter/GemstoneSiteFooter';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import StaticPage from '../PageBuilder/StaticPage';

import css from './AboutPage.module.css';

/* Photography from the GemstoneX design (assets/). about-cta-bg.jpg is the real
   design file. The hero + 3 story plates are temporary emerald stand-ins — those
   four design files are larger than the design tool's 256KB fetch limit, so they
   must be copied in from the design assets/ (same path + names → they swap in
   automatically, no code change). */
import heroImg from './images/about-hero-rough-emerald.jpg';
import story1 from './images/about-story-1-bogota-sorting.jpg';
import story2 from './images/about-story-2-bench-craft.jpg';
import story3 from './images/about-story-3-colombia-source.jpg';
import ctaBg from './images/about-cta-bg.jpg';

const META = {
  title: 'About GemstoneX',
  description:
    'Colombian emeralds, without the guesswork. Trade-grade sourcing infrastructure, on the ground in Bogotá, built for jewellers worldwide.',
  schema: {
    '@context': 'http://schema.org',
    '@type': 'AboutPage',
    name: 'About GemstoneX',
    description:
      'Trade-grade sourcing infrastructure for certified emeralds and coloured stones, on the ground in Bogotá and built for jewellers worldwide.',
  },
};

/* ----------------------------- icons (thin line) — ported from source ---- */
const Arrow = props => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
    <path d="M4 12h15M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IconShield = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    <path d="M9 12l2 2 4-4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IconCert = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="10" cy="9" r="5.5" stroke="currentColor" strokeWidth="1.6" />
    <path d="M7.5 9l1.8 1.8L12.8 7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M7.5 14l-1.5 6 4-2 4 2-1.5-6" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
  </svg>
);
const IconEyeOff = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M4 12s3-6 8-6c1.4 0 2.7.5 3.8 1.2M20 12s-1 2-3 3.6M9.5 9.5a3.5 3.5 0 0 0 5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M4 4l16 16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);
const IconLock = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="5" y="10.5" width="14" height="9.5" rx="2" stroke="currentColor" strokeWidth="1.6" />
    <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

const STEPS = [
  { t: 'Source at origin.', d: 'Stones sourced directly from vetted miners and suppliers in country.' },
  { t: 'Authenticate and grade.', d: 'Each stone independently lab-certified for origin, treatment and quality.' },
  {
    t: 'Verify and list.',
    d: 'Certification and provenance recorded, supplier de-identified, the stone listed with full proof.',
  },
  { t: 'Handle logistics.', d: 'Export, insurance and tax managed across jurisdictions.' },
  {
    t: 'Settle and deliver.',
    d: 'Purchase settled through escrow on regulated rails, funds released on confirmed delivery.',
  },
];

const PILLARS = [
  {
    Icon: IconShield,
    h: 'Verified origin',
    p: 'Provenance established at source and documented against every stone before listing.',
  },
  {
    Icon: IconCert,
    h: 'Lab-certified',
    p: 'Independent grading from recognised laboratories: GIA, GRS, SSEF, Gübelin, AGL, GGTL, CDTEK.',
  },
  {
    Icon: IconEyeOff,
    h: 'Suppliers de-identified',
    p: 'Buyers see the stone and its proof, never the seller. Identity stays hidden throughout.',
  },
  {
    Icon: IconLock,
    h: 'Escrow settlement',
    p: 'Funds held in escrow on regulated banking rails, released on confirmed delivery only.',
  },
];

/* Bogotá hub → Sydney head office, with faint global-reach spokes. */
const FootprintMap = () => {
  const hub = { x: 250, y: 250 };
  const sydney = { x: 700, y: 330 };
  const faint = [
    { x: 120, y: 90 },
    { x: 470, y: 70 },
    { x: 820, y: 150 },
    { x: 150, y: 410 },
  ];
  return (
    <div className={css.fpMap}>
      <svg
        viewBox="0 0 900 460"
        role="img"
        aria-label="Distribution map: Bogotá sourcing hub with a gold spoke to the Sydney head office and faint spokes to unnamed global markets."
      >
        {faint.map((p, i) => (
          <g key={i} className={css.fpFaint}>
            <line x1={hub.x} y1={hub.y} x2={p.x} y2={p.y} stroke="var(--colorPrimary)" strokeWidth="1.5" strokeDasharray="2 7" strokeLinecap="round" />
            <circle cx={p.x} cy={p.y} r="4" fill="var(--colorPrimary)" />
          </g>
        ))}
        <path
          d={`M ${hub.x} ${hub.y} Q ${(hub.x + sydney.x) / 2} ${Math.min(hub.y, sydney.y) - 120} ${sydney.x} ${sydney.y}`}
          fill="none"
          stroke="var(--colorGold)"
          strokeWidth="2"
          strokeDasharray="2 8"
          strokeLinecap="round"
        />
        <circle cx={sydney.x} cy={sydney.y} r="6" fill="none" stroke="var(--colorGold)" strokeWidth="2" />
        <circle cx={sydney.x} cy={sydney.y} r="2.5" fill="var(--colorGold)" />
        <text className={css.fpCity} x={sydney.x + 16} y={sydney.y - 2}>SYDNEY</text>
        <text className={css.fpRole} x={sydney.x + 16} y={sydney.y + 16}>Head office</text>
        <circle cx={hub.x} cy={hub.y} r="11" fill="var(--colorPrimary)" opacity="0.18" />
        <circle cx={hub.x} cy={hub.y} r="6" fill="var(--colorPrimary)" />
        <text className={css.fpCity} x={hub.x} y={hub.y + 34} textAnchor="middle">BOGOTÁ</text>
        <text className={css.fpRole} x={hub.x} y={hub.y + 52} textAnchor="middle">Sourcing &amp; operations</text>
      </svg>
    </div>
  );
};

const AboutPage = () => {
  const layoutAreas = `
    topbar
    main
    footer
  `;

  return (
    <StaticPage {...META}>
      <LayoutComposer areas={layoutAreas} className={css.layout}>
        {props => {
          const { Topbar, Main, Footer } = props;
          return (
            <>
              <Topbar as="header" className={css.topbar}>
                <TopbarContainer currentPage="AboutPage" />
              </Topbar>

              <Main as="main" className={css.main}>
                <div className={css.ab}>
                  {/* Hero */}
                  <header className={css.hero} style={{ '--heroImg': `url(${heroImg})` }}>
                    <div className={`${css.wrap} ${css.heroInner}`}>
                      <div className={css.eyebrow}>About GemstoneX</div>
                      <div className={css.rule} />
                      <h1 className={css.heroTitle}>Colombian emeralds, without the guesswork.</h1>
                      <p className={css.heroSub}>
                        Trade-grade sourcing infrastructure, on the ground in Bogotá, built for
                        jewellers worldwide.
                      </p>
                      <NamedLink name="TradeAccessPage" className={`${css.btn} ${css.btnPrimary}`}>
                        Apply for Trade Access <Arrow />
                      </NamedLink>
                    </div>
                  </header>

                  {/* Our Story */}
                  <section className={css.story} id="story">
                    <div className={css.wrap}>
                      <div className={css.storyGrid}>
                        <aside className={css.storyThesis}>
                          <div className={css.eyebrow}>Our story</div>
                          <div className={css.rule} />
                          <h2 className={css.storyHead}>
                            Built in the coloured gemstone trade. For the people who work in it.
                          </h2>
                          <p className={css.storyCred}>
                            GemstoneX was built by Australians and Colombians who know this trade
                            firsthand, with one foot at the source of the world’s finest emeralds and
                            the other in the markets that buy them.
                          </p>
                          <ul className={css.storyFacts}>
                            <li>
                              <b>01</b> Trade-only, every supplier verified
                            </li>
                            <li>
                              <b>02</b> Every stone certified
                            </li>
                            <li>
                              <b>03</b> One standard: source, assess, cut, photograph
                            </li>
                            <li>
                              <b>04</b> Settled through regulated banking
                            </li>
                          </ul>
                        </aside>

                        <div className={css.storyBody}>
                          <div className={css.mv}>
                            <h3 className={css.mvT}>
                              <span className={css.mvN}>01</span>The trade hasn’t moved in decades
                            </h3>
                            <p>
                              Coloured stones are among the most valued things on earth, yet the way
                              they trade hasn’t moved in decades. Prices are opaque. Provenance is
                              recorded when someone bothers. Treatment, the thing that most determines
                              a stone’s worth, often goes unmentioned until you ask. For a buyer in
                              Sydney or London, real confidence has meant flying to the source or
                              taking someone’s word for it.
                            </p>
                          </div>

                          <figure className={css.plate}>
                            <span className={css.plateTag}>Bogotá · Assessment</span>
                            <img
                              src={story1}
                              alt="A GemstoneX gemmologist grading loose Colombian emeralds at the loupe in the Bogotá office, the city visible through the window"
                            />
                            <figcaption>Bogotá. Every stone graded in-house, to one standard.</figcaption>
                          </figure>

                          <div className={css.mv}>
                            <h3 className={css.mvT}>
                              <span className={css.mvN}>02</span>So we built the other thing
                            </h3>
                            <p>
                              GemstoneX is trade-only: every supplier verified, every stone certified,
                              every transaction documented and settled through regulated banking
                              channels. We source, assess, cut and photograph to one standard, so a
                              jeweller anywhere can judge a stone the way they would in hand. We’re not
                              a dealer chasing your clients. We’re the layer underneath, built so the
                              coloured gemstone market works the way the rest of the professional world
                              already does.
                            </p>
                          </div>

                          <figure className={css.plate}>
                            <span className={css.plateTag}>The bench</span>
                            <img src={story2} alt="A goldsmith torch-soldering a gold ring at the jeweller's bench" />
                            <figcaption>For the people who work in it</figcaption>
                          </figure>

                          <div className={css.mv}>
                            <h3 className={css.mvT}>
                              <span className={css.mvN}>03</span>Closer to where the stones come from
                            </h3>
                            <p>
                              We began with Colombian emeralds, the stones we know best, and we’re
                              building the same infrastructure for coloured gemstones wherever they
                              come from. Because we are Colombian as well as Australian, the people at
                              the origin aren’t an abstraction to us. Shorten the chain, verify who’s
                              in it, formalise what was informal, and value can sit closer to where the
                              stones, and the skill to find them, come from.
                            </p>
                          </div>

                          <figure className={css.plate}>
                            <span className={css.plateTag}>Colombia · At the source</span>
                            <img src={story3} alt="GemstoneX staff alongside miners underground at a Colombian emerald mine" />
                            <figcaption>At the source, where the stones and the skill come from</figcaption>
                          </figure>

                          <p className={css.storyClose}>
                            This is the trade we came from. We’re building the version we always
                            wanted.
                          </p>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* How We Work */}
                  <section className={css.how} id="how">
                    <div className={css.wrap}>
                      <div className={`${css.eyebrow} ${css.howEyebrow}`}>How we work</div>
                      <div className={css.rule} />
                      <h2 className={css.howH2}>From the mine to the market.</h2>
                      <ol className={css.steps}>
                        {STEPS.map((s, i) => (
                          <li className={css.step} key={s.t}>
                            <div className={css.stepNum}>0{i + 1}</div>
                            <div className={css.stepT}>{s.t}</div>
                            <div className={css.stepD}>{s.d}</div>
                          </li>
                        ))}
                      </ol>
                    </div>
                  </section>

                  {/* Certification & Traceability */}
                  <section className={`${css.section} ${css.sectionTint}`} id="certification">
                    <div className={css.wrap}>
                      <div className={css.eyebrow}>Certification &amp; traceability</div>
                      <div className={css.rule} />
                      <h2 className={css.sectionH2}>Proof travels with every stone.</h2>
                      <p className={css.certLede}>
                        Provenance is documented at source and recorded against each stone.
                        Independent laboratories grade origin, treatment and quality. Suppliers stay
                        de-identified, and settlement is held in escrow on regulated banking rails
                        until delivery is confirmed.
                      </p>
                      <div className={css.pillars}>
                        {PILLARS.map(({ Icon, h, p }) => (
                          <div className={css.pillar} key={h}>
                            <div className={css.pillarIcon}>
                              <Icon />
                            </div>
                            <h3>{h}</h3>
                            <p>{p}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </section>

                  {/* Our Footprint */}
                  <section className={css.section} id="footprint">
                    <div className={css.wrap}>
                      <div className={css.eyebrow}>Our footprint</div>
                      <div className={css.rule} />
                      <h2 className={css.sectionH2}>Rooted in Colombia, trusted worldwide.</h2>
                      <div className={css.fpGrid}>
                        <div className={css.fpLeft}>
                          <FootprintMap />
                          <p className={css.fpCaption}>
                            Sourced in Colombia. Supplied to jewellers worldwide, with our head office
                            in Australia.
                          </p>
                        </div>
                        <div className={css.fpRight}>
                          <div className={css.office}>
                            <div className={css.officeLabel}>Sydney · Head office</div>
                            <div className={css.officeName}>GemstoneX Pty Ltd</div>
                            <div className={css.officeReg}>ACN 692 780 117</div>
                            <address className={css.officeAddr}>
                              Level 1, 81–83 Campbell Street, Surry Hills NSW 2010, Australia.
                            </address>
                          </div>
                          <div className={css.office}>
                            <div className={css.officeLabel}>Bogotá · Sourcing</div>
                            <div className={css.officeName}>GemstoneX Col SAS</div>
                            <address className={css.officeAddr}>
                              Carrera 5 No. 15–21, opposite the Emerald Trade Centre, Bogotá D.C.,
                              Colombia.
                            </address>
                          </div>
                          <p className={css.fpContact}>
                            Trade inquiries: <a href="mailto:trade@gemstonex.com">trade@gemstonex.com</a>
                          </p>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Closing CTA band */}
                  <section
                    className={css.cta}
                    style={{
                      '--ctaImg': `url(${ctaBg})`,
                    }}
                  >
                    <div className={css.wrap}>
                      <div className={`${css.eyebrow} ${css.ctaEyebrow}`}>
                        Trade access by application
                      </div>
                      <div className={`${css.rule} ${css.ruleCenter}`} />
                      <h2 className={css.ctaTitle}>
                        The infrastructure for the global gemstone trade.
                      </h2>
                      <NamedLink name="TradeAccessPage" className={`${css.btn} ${css.btnPrimary}`}>
                        Apply for Trade Access <Arrow />
                      </NamedLink>
                    </div>
                  </section>
                </div>
              </Main>

              <Footer>
                <GemstoneSiteFooter showSupplier />
              </Footer>
            </>
          );
        }}
      </LayoutComposer>
    </StaticPage>
  );
};

export default AboutPage;
